import {
  Flex,
  IconButton,
  IconMenuButton,
  Table,
  Text,
  useTableState,
  useToast,
} from '@driftt/dds'
import { faBoxArchive } from '@driftt/dds.icons/light/faBoxArchive'
import { faPenToSquare } from '@driftt/dds.icons/light/faPenToSquare'
import { capitalize } from 'humanize-plus'
import React, { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { RootStateOrAny, useSelector } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { archiveScenario, restoreScenario } from 'api/scenarios'
import { APP_PAGE_TITLES } from 'constants/pageTitles'
import { useGuardrailsList } from 'hooks/useGuardrailsList'
import useDocumentTitle from 'hooks/usePageTitle'

import ConfirmationModal from './ConfirmationModal'

type GuardrailsTableProps = RouteComponentProps & { activeTab?: string }

const GuardrailsTable = ({ history, activeTab }: GuardrailsTableProps) => {
  useDocumentTitle(APP_PAGE_TITLES.GUARDRAIL)

  //May need to update for when we have more guardrails to properly paginate
  const { tableState, handleTableStateChange } = useTableState<
    ReturnType<typeof useGuardrailsList>['guardrails'][number]
  >({
    defaultPagination: {
      pageIndex: 0,
      pageSize: 10,
    },
  })

  const orgId: string = useSelector(({ user }: RootStateOrAny) => user.orgId)

  //Fetches Active Guardrails
  const {
    guardrails,
    isLoading: isLoadingGuardrails,
    refetch: refetchActive,
  } = useGuardrailsList({
    orgId,
  })

  const toast = useToast()

  //Fetches Archived Guardrails
  const { guardrails: archivedGuardrails, refetch: refetchArchived } =
    useGuardrailsList({
      orgId,
      archived: true,
    })

  const [openModal, setOpenModal] = useState<boolean>(false)
  const [action, setAction] = useState<string>('')
  const [selectedGuardrailId, setSelectedGuardrailId] = useState<string>('')
  const [activeRowTotal, setActiveRowTotal] = useState<number>(0)
  const [archivedRowTotal, setArchivedRowTotal] = useState<number>(0)

  useEffect(() => {
    setActiveRowTotal(guardrails?.length || 0)
  }, [guardrails])

  useEffect(() => {
    setArchivedRowTotal(archivedGuardrails?.length || 0)
  }, [archivedGuardrails])

  const GUARDRAILS_TITLE = 'No guardrails found'
  const GUARDRAILS_SUBTITLE = "We couldn't find any guardrails."
  const ARCHIVED_GUARDRAILS_TITLE = 'No archived guardrails yet!'
  const ARCHIVED_GUARDRAILS_SUBTITLE =
    'Any archived guardrails will be found here.'

  const isActiveTab = activeTab?.toLowerCase() === 'active'

  const notCustomizableScenarios = [
    'self identity',
    'identity',
    'no human redirect',
    'meetings',
  ]

  const onArchive = useMutation(archiveScenario, {
    onSuccess: async () => {
      await refetchActive()
      await refetchArchived()
      toast({
        variant: 'success',
        message: 'Guardrail successfully archived.',
      })
      setOpenModal(false)
    },
    onError: () => {
      toast({
        variant: 'error',
        message: `Could not archive guardrail`,
      })
      setOpenModal(false)
    },
  })

  const onRestore = useMutation(restoreScenario, {
    onSuccess: async () => {
      await refetchActive()
      await refetchArchived()
      toast({
        variant: 'success',
        message: `Guardrail successfully restored.`,
      })
      setOpenModal(false)
    },
    onError: () => {
      toast({
        variant: 'error',
        message: `Could not restore guardrail`,
      })
      setOpenModal(false)
    },
  })

  return (
    <>
      <Flex
        fullWidth
        fullHeight
        css={{
          padding: '$3',
          backgroundColor: '$gray2',
          overflow: 'auto',
          paddingBottom: 150,
          zIndex: 0,
        }}
      >
        <Table<ReturnType<typeof useGuardrailsList>['guardrails'][0]>
          emptyState={{
            title: isActiveTab ? GUARDRAILS_TITLE : ARCHIVED_GUARDRAILS_TITLE,
            subtitle: isActiveTab
              ? GUARDRAILS_SUBTITLE
              : ARCHIVED_GUARDRAILS_SUBTITLE,
          }}
          loadingRows={4}
          isLoading={isLoadingGuardrails}
          rows={isActiveTab ? guardrails : archivedGuardrails}
          columns={[
            {
              value: 'name',
              header: 'Guardrail',
              cell: (row) => (
                <Text
                  variant="bodyBold"
                  css={{
                    '&:hover': {
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() =>
                    history.push(`/ai-guardrails/editor/${row.scenarioId}`)
                  }
                >
                  {capitalize(row.name, true)}
                </Text>
              ),
              size: 217,
            },
            {
              value: 'description',
              header: 'Description',
            },
            {
              header: 'Reply guidelines',
              value: 'instruction',
            },
            {
              header: '',
              value: 'editable',
              cell: (
                row: ReturnType<typeof useGuardrailsList>['guardrails'][0]
              ) => (
                <Flex gap={1}>
                  {isActiveTab ? (
                    <>
                      <IconButton
                        icon={faPenToSquare}
                        variant="secondaryError"
                        title="Edit"
                        onClick={() =>
                          history.push(
                            `/ai-guardrails/editor/${row.scenarioId}`
                          )
                        }
                        disabled={notCustomizableScenarios.includes(
                          row.name.toLowerCase()
                        )}
                      />
                      <IconButton
                        icon={faBoxArchive}
                        title="Archive"
                        onClick={() => {
                          setAction('archive')
                          setSelectedGuardrailId(row.scenarioId)
                          setOpenModal(true)
                        }}
                        disabled={
                          notCustomizableScenarios.includes(
                            row.name.toLowerCase()
                          ) || row.name.toLowerCase() === 'moderation'
                        }
                      />
                    </>
                  ) : (
                    <IconMenuButton
                      title=""
                      iconVariant="spillover"
                      options={[
                        {
                          name: 'View',
                          action: () => {
                            history.push(
                              `/ai-guardrails/editor/${row.scenarioId}`
                            )
                          },
                        },
                        {
                          name: 'Restore',
                          action: () => {
                            setAction('restore')
                            setSelectedGuardrailId(row.scenarioId)
                            setOpenModal(true)
                          },
                        },
                      ]}
                    />
                  )}
                </Flex>
              ),
              size: 60,
            },
          ]}
          onStateChange={handleTableStateChange}
          pagination={{
            enableClientSidePagination: true,
            rowTotal: isActiveTab ? activeRowTotal : archivedRowTotal,
            defaultPagination: tableState.pagination,
          }}
        />
      </Flex>
      <ConfirmationModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        onAction={() => {
          action === 'archive'
            ? onArchive.mutate({ scenarioId: selectedGuardrailId, orgId })
            : onRestore.mutate({ scenarioId: selectedGuardrailId, orgId })
        }}
        loading={
          action === 'archive' ? onArchive.isLoading : onRestore.isLoading
        }
        action={action}
      />
    </>
  )
}

export default withRouter(GuardrailsTable)
